// src/components/Pentests/runs/GenericResultsView.js
//
// Fallback view for any scanner without a dedicated layout. Shows result_type,
// value, and a JSON dump of the data column.

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FileJson, Search } from 'lucide-react';

export default function GenericResultsView({ results }) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return results;
    return results.filter((r) =>
      [r.value, r.result_type, JSON.stringify(r.data || {})].some((s) =>
        String(s).toLowerCase().includes(q)
      )
    );
  }, [results, filter]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
        <FileJson className="w-6 h-6 mx-auto mb-2 opacity-60" />
        No results recorded yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter results..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filtered.length === results.length ? `${results.length} results` : `${filtered.length} of ${results.length}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        {filtered.map((r) => {
          const isOpen = expanded.has(r.id);
          const hasData = r.data && Object.keys(r.data).length > 0;
          return (
            <div key={r.id} className="border-b border-cyber-300/30 last:border-b-0">
              <button
                type="button"
                onClick={() => hasData && toggle(r.id)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 ${hasData ? 'hover:bg-cyber-200/40' : 'cursor-default'}`}
              >
                {hasData
                  ? (isOpen ? <ChevronDown className="w-4 h-4 mt-1 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 mt-1 text-slate-500 shrink-0" />)
                  : <span className="w-4 shrink-0" />}
                <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-cyber-200 text-slate-700 font-mono shrink-0">
                  {r.result_type}
                </span>
                <span className="font-mono text-sm text-slate-800 break-all flex-1 min-w-0">
                  {r.value}
                </span>
              </button>
              {isOpen && hasData && (
                <pre className="px-4 pb-4 pl-12 text-xs text-slate-700 overflow-x-auto">
                  {JSON.stringify(r.data, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
